import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { StepState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Target } from '@sorare/core/src/atoms/icons/Target.tsx';
import { Vertical } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { Bold } from '@sorare/core/src/atoms/typography/Bold/index.tsx';
import { BodyS } from '@sorare/core/src/atoms/typography/index.tsx';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge/index.tsx';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel/index.tsx';
import type { HexColor, RGBColor } from '@sorare/core/src/style/types.ts';

import { BracketedScore } from '../BracketedScore/index.tsx';
import { SquadAvatars } from './SquadAvatars/index.tsx';
import { SquadBadge } from './SquadBadge/index.tsx';
import type {
  ScoreBlock_squadStep,
  ScoreBlock_squadBoard,
} from './__generated__/index.graphql.ts';

const Root = styled.div`
  position: relative;
`;
const StyledBezel = styled(Bezel)`
  padding: var(--quadruple-unit);
  position: relative;
  isolation: isolate;
`;
const StyledNeonEdge = styled(NeonEdge)`
  position: absolute;
  inset: 0;
`;
const SquadBadgeWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

const getColors = (
  state: StepState,
  totalLineups: number,
  minimumLineupsToStartStep: number
) => {
  const yellow = {
    base: 'var(--c-yellow-400)' as HexColor,
    rgb: 'var(--c-rgb-yellow-400)' as RGBColor,
  };
  const green = {
    base: 'var(--c-green-400)' as HexColor,
    rgb: 'var(--c-rgb-green-400)' as RGBColor,
  };
  const grey = {
    base: 'var(--c-nd-600)' as HexColor,
    rgb: 'var(--c-rgb-white)' as RGBColor,
  };
  const orange = {
    base: 'var(--c-rewards-orange)' as HexColor,
    rgb: 'var(--c-rgb-rewards-orange)' as RGBColor,
  };
  const lightBlue = {
    base: 'var(--c-score-high)' as HexColor,
    rgb: 'var(--c-rgb-score-high)' as RGBColor,
  };

  if (state === StepState.PLAYABLE) {
    if (totalLineups === 0) {
      return grey;
    }
    if (totalLineups < minimumLineupsToStartStep) {
      return yellow;
    }
    return green;
  }
  if (state === StepState.CLAIMABLE || state === StepState.CLAIMED) {
    return lightBlue;
  }
  if (state === StepState.FAILED) {
    return orange;
  }
  if (state === StepState.LINEUP_SET || state === StepState.LIVE) {
    if (totalLineups < minimumLineupsToStartStep) {
      return yellow;
    }
    return green;
  }
  if (state === StepState.LOCKED) {
    if (totalLineups < minimumLineupsToStartStep) {
      return grey;
    }
    return green;
  }
  return grey;
};

type Props = {
  step: ScoreBlock_squadStep;
  height: number;
  squadBoard?: ScoreBlock_squadBoard;
};
export const ScoreBlock = ({ step, height, squadBoard }: Props) => {
  const { target, state, totalLineups, totalScore, minimumLineupsToStartStep } =
    step;
  const { base: baseColor, rgb: rgbColor } = getColors(
    state,
    totalLineups,
    minimumLineupsToStartStep
  );

  return (
    <Root>
      <StyledBezel
        style={{
          height,
        }}
        background="conic-gradient(from 90deg at 4px 4px, var(--c-nd-50) 25%, var(--c-black) 0) 0 0/5px 5px, var(--c-black)"
        borderColor={`linear-gradient(to bottom, transparent 0%, rgba(${rgbColor}, 0.5) 100%)`}
      >
        <StyledNeonEdge color={baseColor} showHalo="always" />
        <Vertical gap={1} center>
          <BracketedScore score={totalScore} color={baseColor} />
          <BodyS>
            <FormattedMessage
              defaultMessage="Score more than {icon} <b>{target, number}+ PTS</b>"
              values={{ icon: <Target size={1.5} />, target, b: Bold }}
            />
          </BodyS>
          <SquadAvatars step={step} />
        </Vertical>
      </StyledBezel>
      {!!squadBoard && (
        <SquadBadgeWrapper>
          <SquadBadge squadBoard={squadBoard} step={step} />
        </SquadBadgeWrapper>
      )}
    </Root>
  );
};

ScoreBlock.fragments = {
  squadStep: gql`
    fragment ScoreBlock_squadStep on SquadStep {
      id
      target
      state
      totalLineups
      minimumLineupsToStartStep
      totalScore
      ...SquadAvatars_squadStep
      ...SquadBadge_squadStep
    }
    ${SquadAvatars.fragments.squadStep}
    ${SquadBadge.fragments.squadStep}
  ` as TypedDocumentNode<ScoreBlock_squadStep>,
  squadBoard: gql`
    fragment ScoreBlock_squadBoard on SquadBoard {
      id
      ...SquadBadge_squadBoard
    }
    ${SquadBadge.fragments.squadBoard}
  ` as TypedDocumentNode<ScoreBlock_squadBoard>,
};
